import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import useInView from '../hooks/useInView';
import { posts } from '../data/blogPosts';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const Blog = () => (
  <main className="bg-[#05050A] min-h-screen">
    <section className="relative pt-32 pb-16 overflow-hidden">
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-purple-600/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel>Product Notes & Insights</SectionLabel>
        <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-6 tracking-tight leading-[1.05] max-w-3xl">
          The thinking behind<br /><span className="gradient-text">ROMIND KIDS.</span>
        </h1>
        <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
          Short, focused notes on the design decisions, safety philosophy, and product direction behind a platform built for children aged 9–14. Written for parents, partners, and platform reviewers.
        </p>
      </div>
    </section>

    <section data-testid="blog-list-section" className="py-12 pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        {posts.map((p, i) => (
          <FadeIn key={p.slug} delay={i * 70}>
            <Link
              to={`/blog/${p.slug}`}
              data-testid={`blog-post-${i}`}
              className="block glass-card p-6 sm:p-8 hover:border-purple-400/25 transition-all group"
            >
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br ${p.accent} shadow-lg`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[10px] font-outfit font-semibold text-purple-300 tracking-[0.2em] uppercase">{p.category}</span>
                    <span className="text-white/20">·</span>
                    <span className="flex items-center gap-1 text-[10px] text-white/40"><Clock size={10} /> {p.readTime}</span>
                  </div>
                  <h2 className="font-outfit font-bold text-white text-xl sm:text-2xl mb-2 group-hover:text-purple-200 transition-colors">{p.title}</h2>
                  <p className="text-white/55 text-sm leading-relaxed mb-3">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs text-purple-300 font-medium group-hover:gap-2.5 transition-all">
                    Read note <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>
    </section>
  </main>
);

export default Blog;
